const API_BASE = import.meta.env.VITE_API_URL || "";

// Dedupe identical messages within the session so an error that fires in a loop
// (or both window.onerror AND the React boundary catching the same throw) only
// hits the backend once. The backend dedupes again before Telegram.
const seen = new Set<string>();

/**
 * Fire-and-forget report to /v1/client-error. The backend logs it and (in
 * production) forwards it to Telegram. Never throws.
 */
export function reportClientError(message: string, componentStack?: string): void {
  const msg = (message || "Unknown error").slice(0, 500);
  if (seen.has(msg)) return;
  seen.add(msg);
  if (seen.size > 50) seen.clear();
  try {
    fetch(`${API_BASE}/v1/client-error`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        message: msg,
        componentStack: componentStack?.slice(0, 2000),
        location: window.location.pathname,
      }),
      keepalive: true,
    }).catch(() => null);
  } catch {
    // never throw from an error reporter
  }
}

// Third-party noise we never want to alert on: opaque cross-origin script
// errors (the browser strips the detail to "Script error.") and the Facebook
// page-plugin SDK embedded on the homepage, which logs its own caught errors.
function isIgnorableError(message: string): boolean {
  if (!message || message === "Script error." || message === "Script error") return true;
  return /fburl\.com|ErrorUtils|DataStore\.get|connect\.facebook\.net/i.test(message);
}

/**
 * Capture errors that escape React's render tree — async throws, event-handler
 * errors, unhandled promise rejections. Gated to production so dev/HMR noise
 * doesn't spam the chat, and filtered so third-party (e.g. Facebook embed)
 * noise never reaches Telegram. Call once at startup.
 */
export function installGlobalErrorReporting(): void {
  if (!import.meta.env.PROD) return;
  window.addEventListener("error", (e) => {
    // Ignore resource-load errors (img/script 404s) — they have no `error` and
    // target is an element, not the window.
    if (e.target && e.target !== window) return;
    const msg = e.message || String(e.error ?? "");
    if (isIgnorableError(msg)) return;
    reportClientError(msg || "window error");
  });
  window.addEventListener("unhandledrejection", (e) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const msg = reason instanceof Error ? reason.message : String(reason);
    if (isIgnorableError(msg)) return;
    reportClientError(`Unhandled promise rejection: ${msg}`);
  });
}
